import { useCallback, useEffect, useRef, useState } from 'react';
import { useApplicationContext } from 'ApplicationContext';

const ATTACHMENT_SCHEME = 'attachment:';
const REFRESH_BUFFER_MS = 30_000;

interface CachedToken {
  url: string;
  expiresAt: number;
}

/**
 * Resolves attachments referenced via `attachment:{id}` by requesting
 * short-lived access tokens from the backend. Tokens are cached per attachment
 * id for the lifetime of the hook. The hook is keyed by `exampleId` — when the
 * id changes the cache is reset.
 */
export const useAttachmentTokens = (exampleId: string | null | undefined) => {
  const { api, baseUrl } = useApplicationContext();
  const cacheRef = useRef<Map<string, CachedToken>>(new Map());
  const inflightRef = useRef<Map<string, Promise<string | null>>>(new Map());
  const [resolvedVersion, setResolvedVersion] = useState(0);

  useEffect(() => {
    cacheRef.current.clear();
    inflightRef.current.clear();
    setResolvedVersion((v) => v + 1);
  }, [exampleId]);

  const toAbsolute = useCallback((url: string): string => {
    if (!url) return url;
    if (/^(https?:)?\/\//i.test(url)) return url;
    if (!baseUrl) return url;
    return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
  }, [baseUrl]);

  const fetchToken = useCallback(async (attachmentId: string): Promise<string | null> => {
    if (!exampleId) return null;

    const inflight = inflightRef.current.get(attachmentId);
    if (inflight) return inflight;

    const promise = (async () => {
      try {
        const response = await api.productExample.productExampleIssueAttachmentAccessToken(exampleId, attachmentId);
        const { url, expiresAt } = response.data ?? {};
        if (!url || !expiresAt) return null;
        const absolute = toAbsolute(url);
        cacheRef.current.set(attachmentId, {
          url: absolute,
          expiresAt: new Date(expiresAt).getTime(),
        });
        setResolvedVersion((v) => v + 1);
        return absolute;
      } catch {
        return null;
      } finally {
        inflightRef.current.delete(attachmentId);
      }
    })();

    inflightRef.current.set(attachmentId, promise);
    return promise;
  }, [api.productExample, exampleId, toAbsolute]);

  /**
   * Returns the cached token URL for an attachment, or null if not yet
   * resolved. Triggers an async fetch when missing/expired — the component
   * using this hook re-renders via `resolvedVersion` once the token arrives.
   */
  const resolveNow = useCallback((attachmentId: string): string | null => {
    const cached = cacheRef.current.get(attachmentId);
    if (cached && cached.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
      return cached.url;
    }
    void fetchToken(attachmentId);
    return cached?.url ?? null;
  }, [fetchToken]);

  /**
   * Awaits a token URL for the attachment. Hits the cache when fresh,
   * otherwise fires (or joins) the POST and returns when it resolves.
   */
  const awaitToken = useCallback(async (attachmentId: string): Promise<string | null> => {
    const cached = cacheRef.current.get(attachmentId);
    if (cached && cached.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
      return cached.url;
    }
    return fetchToken(attachmentId);
  }, [fetchToken]);

  /**
   * Pre-warms the cache for a set of attachment ids (e.g. on page load).
   */
  const prefetch = useCallback(async (attachmentIds: string[]): Promise<void> => {
    await Promise.all(attachmentIds.map((id) => fetchToken(id)));
  }, [fetchToken]);

  /**
   * Returns a download URL for direct browser consumption (img src / a href).
   * For authenticated-blob downloads use the api client directly.
   */
  const resolveDownloadUrl = useCallback((attachmentId: string, download: boolean): string | null => {
    const base = resolveNow(attachmentId);
    if (!base) return null;
    if (!download) return base;
    return base.includes('?') ? `${base}&download=true` : `${base}?download=true`;
  }, [resolveNow]);

  return {
    resolveNow,
    awaitToken,
    resolveDownloadUrl,
    prefetch,
    resolvedVersion,
  };
};

export const isAttachmentSrc = (src: string | null | undefined): boolean =>
  typeof src === 'string' && src.startsWith(ATTACHMENT_SCHEME);

export const parseAttachmentId = (src: string): string | null => {
  if (!src.startsWith(ATTACHMENT_SCHEME)) return null;
  const id = src.slice(ATTACHMENT_SCHEME.length).trim();
  return id || null;
};

export const buildAttachmentRef = (attachmentId: string): string =>
  `${ATTACHMENT_SCHEME}${attachmentId}`;
